import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { toast } from "react-toastify";
import type { Accept } from "react-dropzone";
import {
  clockInAttendance,
  clockOutAttendance,
  getAttendance,
} from "features/EmployeeAttendance/services/EmployeeAttendanceService";
import type { AttendanceDetails } from "features/EmployeeAttendance/types/employee-attendance.types";
import {
  EmployeeAttendanceLocationType,
  EmployeeAttendanceLocationTypeLabelMap,
} from "features/EmployeeAttendance/constants/employee-attendance.constants";
import { FileDropzone } from "components/common/FileDropzone";

const ATTENDANCE_IMAGE_ACCEPT: Accept = {
  "image/jpeg": [".jpeg", ".jpg"],
  "image/jpg": [".jpg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
};
const ATTENDANCE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayLabel = () =>
  new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

const formatTime = (value: string | null | undefined) => {
  if (!value) return "--:--";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "--:--";
  return parsed.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getDuration = (from: string | null | undefined, to?: string | null) => {
  if (!from) return null;
  const start = new Date(from).getTime();
  const end = to ? new Date(to).getTime() : Date.now();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  const diffMs = end - start;
  if (diffMs < 0) return null;
  const totalMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
};

const getCurrentCoordinates = () =>
  new Promise<{ latitude: number; longitude: number }>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });

type NominatimAddress = {
  road?: string;
  neighbourhood?: string;
  suburb?: string;
  city?: string;
  town?: string;
  village?: string;
  county?: string;
  state?: string;
  postcode?: string;
  country?: string;
};
type NominatimResult = {
  display_name?: string;
  name?: string;
  addresstype?: string;
  address?: NominatimAddress;
};

const formatAddressFromNominatim = (data: NominatimResult): string | null => {
  const display = data.display_name?.trim();
  if (display) return display;
  const a = data.address;
  if (!a) return null;
  const locality =
    a.city?.trim() ||
    a.town?.trim() ||
    a.village?.trim() ||
    a.suburb?.trim() ||
    a.neighbourhood?.trim() ||
    "";
  const parts = [
    data.name?.trim(),
    a.road?.trim(),
    locality,
    a.state?.trim(),
    a.postcode?.trim(),
    a.country?.trim(),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

const reverseGeocode = async (latitude: number, longitude: number) => {
  const url = new URL("https://nominatim.openstreetmap.org/reverse");
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("lat", String(latitude));
  url.searchParams.set("lon", String(longitude));
  url.searchParams.set("zoom", "18");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("accept-language", "en");
  const response = await fetch(url.toString(), {
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    throw new Error("Could not resolve address from coordinates.");
  const data = (await response.json()) as NominatimResult;
  const label = formatAddressFromNominatim(data);
  if (!label) throw new Error("Could not resolve address from coordinates.");
  return label;
};

type AttendanceButtonProps = {
  employeeId?: number | null;
  variant?: "card" | "banner";
  onSuccess?: (inLocationType?: EmployeeAttendanceLocationType) => void;
};

const AttendanceButton = ({
  employeeId,
  variant = "card",
  onSuccess,
}: AttendanceButtonProps) => {
  const [attendance, setAttendance] = useState<AttendanceDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [resolvingAddress, setResolvingAddress] = useState(false);
  const [clockInModalOpen, setClockInModalOpen] = useState(false);
  const [clockOutModalOpen, setClockOutModalOpen] = useState(false);
  const [locationType, setLocationType] =
    useState<EmployeeAttendanceLocationType>(
      EmployeeAttendanceLocationType.OFFICE,
    );
  const [location, setLocation] = useState("");
  const [coordsLabel, setCoordsLabel] = useState<string | null>(null);
  const [images, setImages] = useState<File[]>([]);

  const [outLocationType, setOutLocationType] =
    useState<EmployeeAttendanceLocationType>(
      EmployeeAttendanceLocationType.OFFICE,
    );
  const [outLocation, setOutLocation] = useState("");
  const [outCoordsLabel, setOutCoordsLabel] = useState<string | null>(null);
  const [outLocating, setOutLocating] = useState(false);
  const [outResolvingAddress, setOutResolvingAddress] = useState(false);
  const [outImages, setOutImages] = useState<File[]>([]);
  const [showSessions, setShowSessions] = useState(false);

  const hasEmployee = Number.isFinite(employeeId) && Number(employeeId) > 0;
  const isLocationMissing = location.trim().length === 0;
  const isFieldLocationType =
    locationType === EmployeeAttendanceLocationType.FIELD;
  const isImageMissing = isFieldLocationType && images.length === 0;
  const isOutFieldLocationType =
    outLocationType === EmployeeAttendanceLocationType.FIELD;
  const isOutImageMissing = isOutFieldLocationType && outImages.length === 0;

  const todayDate = getTodayDate();
  const allSessions = attendance
    ? [attendance, ...attendance.sessions].filter((s) => Boolean(s.inTime))
    : [];
  const lastSession = allSessions.length > 0 ? allSessions[allSessions.length - 1] : null;
  const openSession = allSessions.find((s) => !s.outTime) ?? null;
  const isCurrentlyActive = Boolean(openSession);
  const hasClockedInToday = Boolean(attendance?.inTime);

  const fetchTodayAttendance = async () => {
    if (!hasEmployee) return;
    try {
      const response = await getAttendance({
        employeeId: Number(employeeId),
        date: todayDate,
      });
      if (response.success) setAttendance(response.data ?? null);
    } catch {
      setAttendance(null);
    }
  };

  useEffect(() => {
    if (!hasEmployee) {
      setAttendance(null);
      return;
    }
    void (async () => {
      try {
        const response = await getAttendance({
          employeeId: Number(employeeId),
          date: todayDate,
        });
        if (response.success) setAttendance(response.data ?? null);
      } catch {
        setAttendance(null);
      }
    })();
  }, [employeeId, hasEmployee, todayDate]);

  const resetClockInForm = () => {
    setLocationType(EmployeeAttendanceLocationType.OFFICE);
    setLocation("");
    setCoordsLabel(null);
    setImages([]);
  };
  const resetClockOutForm = () => {
    setOutLocationType(EmployeeAttendanceLocationType.OFFICE);
    setOutLocation("");
    setOutCoordsLabel(null);
    setOutImages([]);
  };

  const fillLocationFromGeolocation = async (showErrorToast = true) => {
    let latitude = 0;
    let longitude = 0;
    try {
      setLocating(true);
      const coords = await getCurrentCoordinates();
      latitude = coords.latitude;
      longitude = coords.longitude;
      setCoordsLabel(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
    } catch (err) {
      if (showErrorToast)
        toast.error(
          (err as { message?: string })?.message ??
            "Unable to fetch current location.",
        );
      return;
    } finally {
      setLocating(false);
    }
    try {
      setResolvingAddress(true);
      const address = await reverseGeocode(latitude, longitude);
      setLocation(
        `${address} (${latitude.toFixed(6)}, ${longitude.toFixed(6)})`,
      );
    } catch {
      setLocation(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      if (showErrorToast)
        toast.warn(
          "Could not resolve a street address. Coordinates were used instead.",
        );
    } finally {
      setResolvingAddress(false);
    }
  };

  const fillOutLocationFromGeolocation = async (showErrorToast = true) => {
    let latitude = 0;
    let longitude = 0;
    try {
      setOutLocating(true);
      const coords = await getCurrentCoordinates();
      latitude = coords.latitude;
      longitude = coords.longitude;
      setOutCoordsLabel(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
    } catch (err) {
      if (showErrorToast)
        toast.error(
          (err as { message?: string })?.message ??
            "Unable to fetch current location.",
        );
      return;
    } finally {
      setOutLocating(false);
    }
    try {
      setOutResolvingAddress(true);
      const address = await reverseGeocode(latitude, longitude);
      setOutLocation(
        `${address} (${latitude.toFixed(6)}, ${longitude.toFixed(6)})`,
      );
    } catch {
      setOutLocation(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      if (showErrorToast)
        toast.warn(
          "Could not resolve a street address. Coordinates were used instead.",
        );
    } finally {
      setOutResolvingAddress(false);
    }
  };

  const handleClockInSubmit = async () => {
    if (!hasEmployee) {
      toast.error("Employee profile not linked to this user.");
      return;
    }
    if (isLocationMissing) {
      toast.error("Location is required. Please fetch current location.");
      return;
    }
    if (isImageMissing) {
      toast.error("At least one image is required for field attendance.");
      return;
    }
    try {
      setLoading(true);
      const response = await clockInAttendance({
        employeeId: Number(employeeId),
        inLocationType: locationType,
        inLocation: location.trim() || undefined,
        images: images.length > 0 ? images : undefined,
      });
      if (response.success) {
        toast.success(response.message || "Clocked in successfully.");
        setClockInModalOpen(false);
        resetClockInForm();
        await fetchTodayAttendance();
        onSuccess?.(locationType);
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not update attendance.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClockToggle = () => {
    if (!hasEmployee) {
      toast.error("Employee profile not linked to this user.");
      return;
    }
    if (!isCurrentlyActive) {
      setClockOutModalOpen(false);
      setClockInModalOpen(true);
      return;
    }
    setClockInModalOpen(false);
    setClockOutModalOpen(true);
  };

  const handleClockOutConfirm = async () => {
    if (!hasEmployee) {
      toast.error("Employee profile not linked to this user.");
      return;
    }
    if (isOutImageMissing) {
      toast.error("At least one image is required for field attendance.");
      return;
    }
    try {
      setLoading(true);
      const response = await clockOutAttendance({
        employeeId: Number(employeeId),
        outLocation: outLocation.trim() || null,
        outLocationType,
        outImages: outImages.length > 0 ? outImages : undefined,
      });
      if (response.success) {
        toast.success(response.message || "Clocked out successfully.");
        setClockOutModalOpen(false);
        resetClockOutForm();
        await fetchTodayAttendance();
        onSuccess?.(outLocationType);
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not update attendance.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clockInModalOpen) return;
    if (location.trim()) return;
    void fillLocationFromGeolocation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockInModalOpen]);

  useEffect(() => {
    if (!clockOutModalOpen) return;
    setOutLocationType(
      openSession?.inLocationType ?? EmployeeAttendanceLocationType.OFFICE,
    );
    if (outLocation.trim()) return;
    void fillOutLocationFromGeolocation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockOutModalOpen]);

  const activeDuration = openSession ? getDuration(openSession.inTime) : null;
  const totalWorkMinutes = !isCurrentlyActive ? (attendance?.totalWorkMinutes ?? null) : null;
  const totalDisplay =
    totalWorkMinutes != null
      ? `${Math.floor(totalWorkMinutes / 60)}h ${totalWorkMinutes % 60}m`
      : null;
  const duration = activeDuration ?? totalDisplay;
  const durationLabel = isCurrentlyActive ? `Since: ${duration}` : `Total: ${duration}`;
  const btnColor = isCurrentlyActive ? "danger" : "primary";
  const btnDisabled = !hasEmployee || loading || clockOutModalOpen;

  const clockBtn = (
    <Button
      color={btnColor}
      onClick={handleClockToggle}
      disabled={btnDisabled}
      className={variant === "banner" ? "" : "w-100"}
    >
      {loading ? (
        <>
          <i className="ri-loader-4-line fs-5 align-middle me-1" aria-hidden />
          Please wait...
        </>
      ) : isCurrentlyActive ? (
        <>
          <i
            className="ri-logout-box-r-line fs-5 align-middle me-1"
            aria-hidden
          />
          Clock Out
        </>
      ) : (
        <>
          <i className="ri-login-box-line fs-5 align-middle me-1" aria-hidden />
          Clock In
        </>
      )}
    </Button>
  );

  const modals = (
    <>
      <Modal
        isOpen={clockInModalOpen}
        toggle={() => {
          setClockInModalOpen(false);
          resetClockInForm();
        }}
      >
        <ModalHeader
          toggle={() => {
            setClockInModalOpen(false);
            resetClockInForm();
          }}
        >
          Clock In Details
        </ModalHeader>
        <ModalBody>
          <Label for="attendanceLocationType" className="form-label mb-1">
            Location Type
          </Label>
          <Input
            id="attendanceLocationType"
            type="select"
            value={locationType}
            onChange={(e) =>
              setLocationType(e.target.value as EmployeeAttendanceLocationType)
            }
            disabled={loading}
          >
            {Object.values(EmployeeAttendanceLocationType).map((v) => (
              <option key={v} value={v}>
                {EmployeeAttendanceLocationTypeLabelMap[v]}
              </option>
            ))}
          </Input>
          <Label for="attendanceLocationText" className="form-label mb-1 mt-3">
            Location
          </Label>
          <Input
            id="attendanceLocationText"
            type="text"
            value={location}
            placeholder="Resolved from your current location"
            readOnly
            disabled={loading || locating || resolvingAddress}
          />
          {coordsLabel ? (
            <p className="text-muted small mb-0 mt-1">
              Coordinates: {coordsLabel}
            </p>
          ) : null}
          {resolvingAddress ? (
            <p className="text-muted small mb-0 mt-1">Resolving address…</p>
          ) : null}
          <div className="mt-2">
            <Button
              color="light"
              size="sm"
              onClick={() => void fillLocationFromGeolocation(true)}
              disabled={loading || locating || resolvingAddress}
            >
              {locating ? (
                <>
                  <i className="ri-loader-4-line align-middle me-1" />
                  Detecting...
                </>
              ) : resolvingAddress ? (
                <>
                  <i className="ri-map-pin-line align-middle me-1" />
                  Resolving address...
                </>
              ) : (
                <>
                  <i className="ri-map-pin-user-line align-middle me-1" />
                  Use Current Location
                </>
              )}
            </Button>
          </div>
          {isLocationMissing ? (
            <p className="text-danger small mb-0 mt-1">
              Location is required for clock in.
            </p>
          ) : null}
          <Label className="form-label mb-1 mt-3">
            Images{" "}
            {isFieldLocationType ? (
              <span className="text-danger ms-1">*</span>
            ) : null}
          </Label>
          <FileDropzone
            accept={ATTENDANCE_IMAGE_ACCEPT}
            maxSize={ATTENDANCE_IMAGE_MAX_BYTES}
            multiple
            compact
            disabled={loading}
            invalid={isImageMissing}
            errorText="At least one image is required for field attendance."
            description="JPEG, JPG, PNG, WEBP — max 5 MB each, up to 5 images"
            onDropAccepted={(files) => {
              const next = [...images, ...files].slice(0, 5);
              if (images.length + files.length > 5)
                toast.error("Maximum 5 images allowed.");
              setImages(next);
            }}
          />
          {images.length > 0 ? (
            <div className="mt-2 d-flex flex-wrap gap-2">
              {images.map((file, i) => (
                <div
                  key={i}
                  className="position-relative"
                  style={{ width: 56, height: 56 }}
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: "cover",
                      borderRadius: 6,
                      border: "1px solid #dee2e6",
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      fontSize: 10,
                      transform: "translate(40%, -40%)",
                    }}
                    onClick={() =>
                      setImages(images.filter((_, idx) => idx !== i))
                    }
                    aria-label="Remove image"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => {
              setClockInModalOpen(false);
              resetClockInForm();
            }}
            disabled={loading}
          >
            <i className="ri-close-line align-middle me-1" />
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleClockInSubmit}
            disabled={
              loading ||
              locating ||
              resolvingAddress ||
              isLocationMissing ||
              isImageMissing
            }
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line align-middle me-1" />
                Please wait...
              </>
            ) : (
              <>
                <i className="ri-login-box-line align-middle me-1" />
                Confirm Clock In
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={clockOutModalOpen}
        toggle={() => {
          if (!loading) {
            setClockOutModalOpen(false);
            resetClockOutForm();
          }
        }}
      >
        <ModalHeader
          toggle={() => {
            if (!loading) {
              setClockOutModalOpen(false);
              resetClockOutForm();
            }
          }}
        >
          Clock Out
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">
            You clocked in at{" "}
            <span className="fw-medium">{formatTime(openSession?.inTime)}</span>.
            {(openSession?.inLocation ?? openSession?.location)?.trim() ? (
              <span className="text-muted ms-1 small">
                ({(openSession?.inLocation ?? openSession?.location)?.trim()})
              </span>
            ) : null}
          </p>
          <Label for="outLocationType" className="form-label mb-1">
            Location Type
          </Label>
          <Input
            id="outLocationType"
            type="select"
            value={outLocationType}
            onChange={(e) =>
              setOutLocationType(
                e.target.value as EmployeeAttendanceLocationType,
              )
            }
            disabled={loading}
          >
            {Object.values(EmployeeAttendanceLocationType).map((v) => (
              <option key={v} value={v}>
                {EmployeeAttendanceLocationTypeLabelMap[v]}
              </option>
            ))}
          </Input>
          <Label for="outLocationText" className="form-label mb-1 mt-3">
            Clock-out Location
          </Label>
          <Input
            id="outLocationText"
            type="text"
            value={outLocation}
            placeholder="Resolved from your current location"
            readOnly
            disabled={loading || outLocating || outResolvingAddress}
          />
          {outCoordsLabel ? (
            <p className="text-muted small mb-0 mt-1">
              Coordinates: {outCoordsLabel}
            </p>
          ) : null}
          {outResolvingAddress ? (
            <p className="text-muted small mb-0 mt-1">Resolving address…</p>
          ) : null}
          <div className="mt-2">
            <Button
              color="light"
              size="sm"
              onClick={() => void fillOutLocationFromGeolocation(true)}
              disabled={loading || outLocating || outResolvingAddress}
            >
              {outLocating ? (
                <>
                  <i className="ri-loader-4-line align-middle me-1" />
                  Detecting...
                </>
              ) : outResolvingAddress ? (
                <>
                  <i className="ri-map-pin-line align-middle me-1" />
                  Resolving address...
                </>
              ) : (
                <>
                  <i className="ri-map-pin-user-line align-middle me-1" />
                  Use Current Location
                </>
              )}
            </Button>
          </div>
          <Label className="form-label mb-1 mt-3">
            Images{" "}
            {isOutFieldLocationType ? (
              <span className="text-danger ms-1">*</span>
            ) : null}
          </Label>
          <FileDropzone
            accept={ATTENDANCE_IMAGE_ACCEPT}
            maxSize={ATTENDANCE_IMAGE_MAX_BYTES}
            multiple
            compact
            disabled={loading}
            invalid={isOutImageMissing}
            errorText="At least one image is required for field attendance."
            description="JPEG, JPG, PNG, WEBP — max 5 MB each, up to 5 images"
            onDropAccepted={(files) => {
              const next = [...outImages, ...files].slice(0, 5);
              if (outImages.length + files.length > 5)
                toast.error("Maximum 5 images allowed.");
              setOutImages(next);
            }}
          />
          {outImages.length > 0 ? (
            <div className="mt-2 d-flex flex-wrap gap-2">
              {outImages.map((file, i) => (
                <div
                  key={i}
                  className="position-relative"
                  style={{ width: 56, height: 56 }}
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: "cover",
                      borderRadius: 6,
                      border: "1px solid #dee2e6",
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      fontSize: 10,
                      transform: "translate(40%, -40%)",
                    }}
                    onClick={() =>
                      setOutImages(outImages.filter((_, idx) => idx !== i))
                    }
                    aria-label="Remove image"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => {
              setClockOutModalOpen(false);
              resetClockOutForm();
            }}
            disabled={loading}
          >
            <i className="ri-close-line align-middle me-1" />
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleClockOutConfirm}
            disabled={
              loading || outLocating || outResolvingAddress || isOutImageMissing
            }
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line align-middle me-1" />
                Please wait...
              </>
            ) : (
              <>
                <i className="ri-logout-box-r-line align-middle me-1" />
                Confirm Clock Out
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );

  if (variant === "banner") {
    return (
      <>
        <Card className="mb-4">
          <CardBody className="py-3">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <div>
                <div className="fw-semibold">Today's Attendance</div>
                <div className="text-muted small">{getTodayLabel()}</div>
              </div>

              <div className="vr d-none d-sm-block" style={{ height: 36 }} />

              {duration ? (
                <>
                  <div
                    className="vr d-none d-sm-block"
                    style={{ height: 36 }}
                  />
                  <span className="text-muted small">
                    {durationLabel}
                  </span>
                </>
              ) : null}

              <div className="d-flex gap-4">
                <div>
                  <div className="text-muted small mb-1">
                    <i className="ri-login-box-line me-1" />
                    Clock In
                  </div>
                  <div
                    className={`fw-semibold ${hasClockedInToday ? "text-success" : "text-muted"}`}
                  >
                    {formatTime(openSession?.inTime ?? lastSession?.inTime)}
                  </div>
                </div>
                <div>
                  <div className="text-muted small mb-1">
                    <i className="ri-logout-box-r-line me-1" />
                    Clock Out
                  </div>
                  <div
                    className={`fw-semibold ${!isCurrentlyActive && hasClockedInToday ? "text-danger" : "text-muted"}`}
                  >
                    {formatTime(isCurrentlyActive ? null : lastSession?.outTime)}
                  </div>
                </div>
              </div>

              <div className="ms-auto">{clockBtn}</div>
            </div>
            {attendance && attendance.sessions.length > 0 ? (
              <div className="border-top mt-2 pt-2">
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-muted d-flex align-items-center gap-1"
                  style={{ fontSize: "0.78rem", textDecoration: "none" }}
                  onClick={() => setShowSessions((v) => !v)}
                >
                  <i className={`ri-${showSessions ? "arrow-up" : "arrow-down"}-s-line`} />
                  {attendance.sessions.length + 1} sessions today
                </button>
                {showSessions ? (
                  <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
                    {[attendance, ...attendance.sessions]
                      .filter((s) => Boolean(s.inTime))
                      .map((s, i) => (
                        <span key={i} className="text-muted small">
                          {i > 0 && <span className="me-2 opacity-50">·</span>}
                          <span className="text-success fw-medium">{formatTime(s.inTime)}</span>
                          {" – "}
                          {s.outTime ? (
                            <span className="text-danger fw-medium">{formatTime(s.outTime)}</span>
                          ) : (
                            <em className="text-primary">now</em>
                          )}
                          {getDuration(s.inTime, s.outTime ?? undefined) ? (
                            <span className="text-muted ms-1">
                              ({getDuration(s.inTime, s.outTime ?? undefined)})
                            </span>
                          ) : null}
                        </span>
                      ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </CardBody>
        </Card>
        {modals}
      </>
    );
  }

  return (
    <>
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="card-title mb-0">Attendance</h5>
            <span className="text-muted small">{getTodayLabel()}</span>
          </div>

          {duration ? (
            <div className="mb-3">
              <span className="text-muted small">
                {durationLabel}
              </span>
            </div>
          ) : null}

          <div className="row g-2 mb-2">
            <div className="col-6">
              <div className="border rounded p-2 text-center bg-light">
                <div className="text-muted small mb-1">
                  <i className="ri-login-box-line me-1" />
                  Clock In
                </div>
                <div
                  className={`fw-semibold ${hasClockedInToday ? "text-success" : "text-muted"}`}
                >
                  {formatTime(openSession?.inTime ?? lastSession?.inTime)}
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="border rounded p-2 text-center bg-light">
                <div className="text-muted small mb-1">
                  <i className="ri-logout-box-r-line me-1" />
                  Clock Out
                </div>
                <div
                  className={`fw-semibold ${!isCurrentlyActive && hasClockedInToday ? "text-danger" : "text-muted"}`}
                >
                  {formatTime(isCurrentlyActive ? null : lastSession?.outTime)}
                </div>
              </div>
            </div>
          </div>

          {attendance && attendance.sessions.length > 0 ? (
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-muted d-flex align-items-center gap-1"
                style={{ fontSize: "0.78rem", textDecoration: "none" }}
                onClick={() => setShowSessions((v) => !v)}
              >
                <i className={`ri-${showSessions ? "arrow-up" : "arrow-down"}-s-line`} />
                {attendance.sessions.length + 1} sessions today
              </button>
              {showSessions ? (
                <div className="mt-2">
                  {[attendance, ...attendance.sessions]
                    .filter((s) => Boolean(s.inTime))
                    .map((s, i) => (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between py-2 border-bottom"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="badge bg-light text-muted border"
                            style={{ fontSize: "0.65rem" }}
                          >
                            #{i + 1}
                          </span>
                          <span className="small">
                            <span className="text-success fw-medium">
                              {formatTime(s.inTime)}
                            </span>
                            {" → "}
                            {s.outTime ? (
                              <span className="text-danger fw-medium">
                                {formatTime(s.outTime)}
                              </span>
                            ) : (
                              <span className="text-primary fst-italic">now</span>
                            )}
                          </span>
                        </div>
                        <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                          {getDuration(s.inTime, s.outTime ?? undefined) ?? "…"}
                        </span>
                      </div>
                    ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="mb-3" />
          )}

          {clockBtn}
        </CardBody>
      </Card>
      {modals}
    </>
  );
};

export default AttendanceButton;
